import { type JSX } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Table } from "react-bootstrap";
import { toast } from "react-toastify";

import NoDataFound from "src/components/common/NoDataFound";
import {
  getRecycleBinDashboardsApi,
  restoreDashboardApi,
} from "src/api/dashboardApi";
import type { RecycleBinDashboardsResponse } from "src/types/dashboard";

export default function RecycleBinPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: recycleBin, isLoading } = useQuery<
    RecycleBinDashboardsResponse,
    Error
  >({
    queryKey: ["dashboards", "recycle-bin"],
    queryFn: () => getRecycleBinDashboardsApi(),
    staleTime: 0,
  });

  const {
    mutate: restoreDashboard,
    isPending: isRestoring,
    variables: restoringCode,
  } = useMutation({
    mutationFn: (code: string) => restoreDashboardApi(code),
    onSuccess: (data, code) => {
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
      // Drop (not just invalidate) any cached dashboard-detail query for this
      // code. If it was open before being deleted, react-query kept the old
      // "error" query result (data from before the delete, status: "error")
      // around instead of clearing it — invalidating alone re-marks it stale
      // but keeps that stale error result on the next mount, so the page
      // would flash "Failed to load dashboard" before the background
      // refetch resolves. Removing it outright forces a clean "pending"
      // state on first render instead.
      queryClient.removeQueries({ queryKey: ["dashboard_name", code] });
      if (data.restored_dashboard_code !== code) {
        queryClient.removeQueries({
          queryKey: ["dashboard_name", data.restored_dashboard_code],
        });
      }
      toast.success(
        data.renamed
          ? "Dashboard restored (renamed to avoid a naming conflict)."
          : "Dashboard restored.",
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to restore dashboard.");
    },
  });

  const dashboards = recycleBin?.dashboards ?? [];

  return (
    <div className="container-fluid px-5 py-3 overflow-y-auto overflow-x-hidden">
      <div className="row my-3 align-items-center">
        <div className="col-12 d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-light"
            aria-label="Go back"
            title="Go back"
            onClick={() => navigate(-1)}
          >
            <i className="bi bi-arrow-left" />
          </button>
          <h1 className="auth-title mb-0">Recycle Bin</h1>
        </div>
      </div>

      <div className="card shadow-sm p-3">
        <Table striped hover responsive>
          <thead>
            <tr className="text-center">
              <th>Name</th>
              <th style={{ whiteSpace: "nowrap" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              [...Array(5).keys()].map((i) => (
                <tr key={i}>
                  <td className="placeholder-glow">
                    <span className="placeholder placeholder-sm w-100" />
                  </td>
                  <td className="placeholder-glow">
                    <span className="placeholder placeholder-sm w-100" />
                  </td>
                </tr>
              ))
            ) : dashboards.length > 0 ? (
              dashboards.map((dashboard) => (
                <tr key={dashboard.code}>
                  <td className="text-center">{dashboard.name}</td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success"
                      onClick={() => restoreDashboard(dashboard.code)}
                      disabled={isRestoring && restoringCode === dashboard.code}
                    >
                      {isRestoring && restoringCode === dashboard.code
                        ? "Restoring..."
                        : "Restore"}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} className="bg-white border-0">
                  <NoDataFound />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}
